import { ArrowLeft, Check } from 'lucide-react';
import { useState } from 'react';
import { putBotProfile } from '../../api/endpoints';
import type { BotProfile } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { BOT_PRESETS, type BotPreset } from './presets';

/**
 * The two questions a new assistant cannot start without: what it is for, and
 * in whose words. Everything it writes is an ordinary profile the Assistant
 * section can edit afterwards — the wizard has no privileges and leaves nothing
 * behind, so abandoning it costs nothing.
 *
 * It exists because the old empty state asked for none of this: one button
 * dropped the operator into a blank Business context box with no hint that the
 * box is what decides whether the bot is any good.
 *
 * Where it answers used to be the middle step, and with no inbox yet it sent
 * the operator off to the Connect screen with the wizard thrown away. It is the
 * screen's Install section now, which creates the website chat in place — the
 * wizard lands there.
 */
export function BotSetupWizard({ onCreated }: { onCreated: (profile: BotProfile) => void }) {
  const [step, setStep] = useState(0);
  const [preset, setPreset] = useState<BotPreset | null>(null);
  const [name, setName] = useState('Assistant');
  const [context, setContext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = (next: BotPreset) => {
    setPreset(next);
    setContext(next.systemContext);
    setStep(1);
  };

  const create = async () => {
    if (!preset) return;
    setBusy(true);
    setError(null);
    try {
      const profile = await putBotProfile({
        name: name.trim() || 'Assistant',
        // Off until the operator has read what it will say. The switch at the
        // top of the screen is the deliberate second act.
        enabled: false,
        systemContext: context.trim() || preset.systemContext,
        handoffMessage: preset.handoffMessage,
        resolutionMessage: preset.resolutionMessage,
        flags: preset.flags as Record<string, unknown>,
        guardrails: preset.guardrails,
      });
      onCreated(profile);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the assistant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card bot-wizard">
      <ol className="bot-steps">
        {['What it does', 'How it sounds', 'Put it on your site'].map((label, index) => (
          <li key={label} className={index === step ? 'is-current' : index < step ? 'is-done' : ''}>
            <span className="bot-step-dot">{index < step ? <Check size={12} /> : index + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      {error && <ErrorNote error={error} />}

      {step === 0 && (
        <>
          <h2 className="manage-card-title">What should it do?</h2>
          <p className="manage-card-sub">
            This picks the starting instructions, guardrails and capabilities. All of it is editable
            afterwards.
          </p>
          <div className="bot-preset-grid">
            {BOT_PRESETS.map((option) => (
              <button
                key={option.id}
                type="button"
                className="card bot-preset"
                onClick={() => choose(option)}
              >
                <span className="bot-preset-label">{option.label}</span>
                <span className="bot-preset-blurb">{option.blurb}</span>
                <span className="bot-preset-effects">
                  {option.effects.map((effect) => (
                    <span key={effect} className="bot-preset-chip">
                      {effect}
                    </span>
                  ))}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 1 && preset && (
        <>
          <h2 className="manage-card-title">How should it sound?</h2>
          <Field label="Name" hint="What customers see it called.">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field
            label="Business context"
            hint="Who you are, what you offer, tone. The assistant's standing instructions."
          >
            <textarea
              className="input"
              rows={7}
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </Field>
          <h3 className="bot-subhead">It will start with these guardrails</h3>
          <ul className="bot-guardrail-preview">
            {preset.guardrails.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          <Note tone="info">
            It is created switched <strong>off</strong>. Next you put it on your website; read what it says
            in the Playground, then switch it on at the top of the screen.
          </Note>
          <div className="manage-card-actions manage-card-actions-split">
            <button className="btn btn-ghost" onClick={() => setStep(0)} disabled={busy}>
              <ArrowLeft size={14} />
              Back
            </button>
            <button className="btn btn-primary" onClick={() => void create()} disabled={busy}>
              {busy && <Spinner />}
              Create and put it on my site
            </button>
          </div>
        </>
      )}
    </section>
  );
}
